import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { withLaunchDarkly, type LaunchDarklyFlags } from '@/common/launchDarkly';
import { TemplatePickerShelfDrawer } from '@/components/senderBuild/template-picker-shelf-drawer';
import useUserInfo from '../../auth/useUserInfo';
import Done from '../../components/senderBuild/Done';
import { ResourceNames } from '../../lib/constants';
import { useIsMediumScreenMediaQuery, useIsSmallScreenMediaQuery } from '../../lib/isSmallScreen';
import { useDispatch, useShallowSelector } from '../../lib/reduxHooks';
import { UserOrigins } from '../../lib/types';
import * as actions from '../../store/senderBuild/actions';
import { EnvelopeSent } from '../envelope-sent/EnvelopeSent';
import { BuilderLayout } from './builder-layout';
import { EnvelopeBuilderFooter } from './envelope-builder-footer';
import { useFetchEnvelopeInfoAndRecipients } from './use-fetch-envelope-info-and-recipients';
import { useMobileAppSignNow } from './use-mobile-app-sign-now';
import { useSetDefaultZoom } from './use-set-default-zoom';

function EnvelopeBuilderPage(props: { flags: LaunchDarklyFlags }) {
  const { flags } = props;
  const { id: queryParamEnvelopeId } = useParams<{ id: string }>();

  const state = useShallowSelector((state) => ({
    envelopeId: state.senderBuild.id ?? '',
    done: state.senderBuild.done,
    recipientsFetched: state.senderBuild.recipientsFetched,
    settings: state.pageFrame.settings,
    status: state.senderBuild.status,
    fetchError: state.senderBuild.fetchError,
    selectedDocumentId: state.senderBuild.currentDocument,
  }));

  const dispatch = useDispatch();
  const history = useHistory();
  const userInfo = useUserInfo();
  const isMdScreenOrSmaller = useIsMediumScreenMediaQuery();
  const isSmScreenOrSmaller = useIsSmallScreenMediaQuery();

  const [isTemplatePickerOpen, setIsTemplatePickerOpen] = React.useState(false);

  const setDefaultZoom = useSetDefaultZoom({ isSmallScreen: isSmScreenOrSmaller, isFullScreen: isMdScreenOrSmaller });
  useFetchEnvelopeInfoAndRecipients({
    envelopeId: queryParamEnvelopeId,
    resource: ResourceNames.ENVELOPE,
    setDefaultZoom,
    userInfo,
  });

  useMobileAppSignNow({ flags });

  React.useEffect(() => {
    // Setting the initial state for the store state
    dispatch(actions.reset());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, []);

  //
  // If there is a change in userInfo that is not undefined and envelope info was fetched,
  // it means that the token was reset
  // Reset the state to re-trigger the calls to get envelope info and recipients
  //
  React.useEffect(() => {
    if (userInfo?.provisioned && state.envelopeId) {
      dispatch(actions.reset());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [userInfo]);

  if (state.fetchError) {
    history.push('/');
  }
  if (!state.recipientsFetched) {
    return null;
  }

  if (state.done) {
    switch (state.settings?.account.userOrigin) {
      case UserOrigins.DOP:
        return <Done />;
      default:
        return <EnvelopeSent flags={flags} />;
    }
  }

  if (
    (state.status === 'Sent' || state.status === 'InProgress') &&
    state.settings?.account.userOrigin !== UserOrigins.DOP
  ) {
    return <EnvelopeSent flags={flags} />;
  }

  function handleApplyTemplateDrawerOpen() {
    setIsTemplatePickerOpen(true);
    dispatch(actions.applyTemplateToDocumentId(state.selectedDocumentId!));
  }

  return (
    <BuilderLayout
      flags={flags}
      footer={<EnvelopeBuilderFooter flags={flags} onOpenApplyTemplateDrawer={handleApplyTemplateDrawerOpen} />}
    >
      <TemplatePickerShelfDrawer
        envelopeId={state.envelopeId}
        show={isTemplatePickerOpen}
        close={() => setIsTemplatePickerOpen(false)}
      />
    </BuilderLayout>
  );
}
const withLd = withLaunchDarkly(EnvelopeBuilderPage);
export { withLd as EnvelopeBuilderPage };
